import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { getDashboard } from 'redux/DashboardSliceV2';
import { getBlocks } from 'redux/ModelsSlice';
import {
  revampModalCloseButtonStyles,
  revampModalFooterStyles,
  revampModalPrimaryButtonStyles,
  revampModalSecondaryButtonStyles,
} from 'styles/revampFormFieldStyles';
import { font, fontSize, neutral, radius, shadow, space, text } from 'styles/revampTokens';

import DeliveryDialog from './serverPack/DeliveryDialog';
import DeliveryScheduleList from './serverPack/DeliveryScheduleList';
import { exportPackArtifact, type PackExportFormat } from './serverPack/exportPackPdf';
import { listDeliveries, type DeliveryRecord } from './serverPack/maestroDeliveryClient';
import usePackRenderContext from './serverPack/usePackRenderContext';
import ExportPackSummary from './serverPack/ExportPackSummary';
import ExportPanelTabBar, { type ExportPanelTab } from './ExportPanelTabBar';

const packExportErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const { response } = error as { response?: { data?: { detail?: string } } };
    if (typeof response?.data?.detail === 'string') return response.data.detail;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
};

export interface DashboardExportPanelProperties {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Server-side pack export: download a PDF now or configure automatic email delivery.
 * Shown when LaunchDarkly `ReportPacks` is on (replaces client Kendo export flow).
 */
const DashboardExportPanel = ({ isOpen, onClose }: DashboardExportPanelProperties) => {
  const toast = useToast();
  const model = useSelector(getBlocks);
  const dashboard = useSelector(getDashboard);
  const renderContext = usePackRenderContext();

  const [activeTab, setActiveTab] = useState<ExportPanelTab>('download');
  const [busy, setBusy] = useState<PackExportFormat | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([]);
  const [deliveriesLoaded, setDeliveriesLoaded] = useState(false);
  const [editingDeliveryId, setEditingDeliveryId] = useState<string | null>(null);
  const deliveryModal = useDisclosure();

  const modelId = model?.id != null ? Number(model.id) : Number.NaN;
  const dashboardId = dashboard?.id != null ? String(dashboard.id) : '';
  const dashboardName = dashboard?.name ?? 'Dashboard';

  const packDeliveries = useMemo(
    () =>
      deliveries.filter(
        (row) => dashboardId && String(row.source_ref?.id ?? '') === String(dashboardId),
      ),
    [deliveries, dashboardId],
  );

  const editingDelivery = useMemo(() => {
    if (!editingDeliveryId) return null;
    return deliveries.find((row) => row.id === editingDeliveryId) ?? null;
  }, [deliveries, editingDeliveryId]);

  const loadDeliveries = useCallback(async () => {
    if (!Number.isFinite(modelId)) return;
    try {
      const rows = await listDeliveries(modelId);
      setDeliveries(Array.isArray(rows) ? rows : []);
    } catch {
      setDeliveries([]);
    } finally {
      setDeliveriesLoaded(true);
    }
  }, [modelId]);

  useEffect(() => {
    if (!isOpen) return;
    setStatusMessage(null);
    setActiveTab('download');
    setDeliveriesLoaded(false);
    loadDeliveries().catch(() => {});
  }, [isOpen, loadDeliveries]);

  useEffect(() => {
    if (!deliveryModal.isOpen) return;
    loadDeliveries().catch(() => {});
  }, [deliveryModal.isOpen, loadDeliveries]);

  const openDeliveryModal = (deliveryId: string | null) => {
    setEditingDeliveryId(deliveryId);
    deliveryModal.onOpen();
  };

  const downloadPack = async (format: PackExportFormat) => {
    if (!dashboardId) return;
    const formatLabel = format === 'pdf' ? 'PDF' : 'Excel';
    setBusy(format);
    setStatusMessage(null);
    try {
      const result = await exportPackArtifact(dashboardId, dashboardName, renderContext, format);
      setStatusMessage(`Downloaded ${result.filename}`);
      toast({
        status: 'success',
        title: `${formatLabel} ready`,
        description: result.filename,
      });
      if (result.previewUrl && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(result.previewUrl);
      }
    } catch (error) {
      toast({
        status: 'error',
        title: `${formatLabel} export failed`,
        description: packExportErrorMessage(error),
      });
    } finally {
      setBusy(null);
    }
  };

  const canExport = Boolean(dashboardId) && Number.isFinite(modelId);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered blockScrollOnMount={false}>
        <ModalOverlay />
        <ModalContent
          width='560px'
          maxWidth='92vw'
          p='20px'
          borderRadius={radius.surface}
          boxShadow={shadow.overlay}
          fontFamily={font.body}
        >
          <ModalHeader
            p={0}
            pb={space['2xl']}
            mb={space.md}
            fontFamily={font.display}
            fontSize='18px'
            fontWeight={600}
            color={text.primary}
          >
            Export report pack
          </ModalHeader>
          <ModalCloseButton top='16px' right='16px' {...revampModalCloseButtonStyles} />
          <ModalBody p={0}>
            {!canExport ? (
              <Text color={text.secondary} fontSize={fontSize.body}>
                Open a dashboard with pages before exporting.
              </Text>
            ) : (
              <Flex direction='column' gap={4}>
                <ExportPackSummary />

                <ExportPanelTabBar activeTab={activeTab} onChange={setActiveTab} />

                {activeTab === 'download' && (
                  <Box
                    border={`1px solid ${neutral.border}`}
                    borderRadius={radius.control}
                    p={4}
                    bg={neutral.surface}
                  >
                    <Text fontSize={fontSize.body} color={text.secondary}>
                      Refresh the pack snapshot and download a PDF or Excel workbook generated on
                      the server.
                    </Text>
                    {statusMessage && (
                      <Text mt={2} fontSize={fontSize.label} color={text.primary}>
                        {statusMessage}
                      </Text>
                    )}
                  </Box>
                )}

                {activeTab === 'distribution' && (
                  <Box
                    border={`1px solid ${neutral.border}`}
                    borderRadius={radius.control}
                    p={4}
                    bg={neutral.surface}
                  >
                    <Text fontSize={fontSize.body} color={text.secondary} mb={4}>
                      Email this pack on a daily, weekly, or monthly schedule. Use Test now in the
                      editor to send a PDF to yourself.
                    </Text>
                    <DeliveryScheduleList
                      deliveries={packDeliveries}
                      loading={!deliveriesLoaded}
                      selectedId={deliveryModal.isOpen ? editingDeliveryId : null}
                      onEdit={(id) => openDeliveryModal(id)}
                      onCreateNew={() => openDeliveryModal(null)}
                      showHeaderCreate={false}
                    />
                  </Box>
                )}
              </Flex>
            )}
          </ModalBody>
          {canExport && (
            <ModalFooter {...revampModalFooterStyles} borderTop='none' pt={0}>
              {activeTab === 'download' && (
                <>
                  <Button
                    onClick={() => {
                      downloadPack('pdf').catch(() => {});
                    }}
                    isLoading={busy === 'pdf'}
                    isDisabled={busy !== null}
                    {...revampModalPrimaryButtonStyles}
                  >
                    Download PDF
                  </Button>
                  <Button
                    variant='outline'
                    onClick={() => {
                      downloadPack('xlsx').catch(() => {});
                    }}
                    isLoading={busy === 'xlsx'}
                    isDisabled={busy !== null}
                    {...revampModalSecondaryButtonStyles}
                  >
                    Download Excel
                  </Button>
                </>
              )}
              {activeTab === 'distribution' && (
                <Button onClick={() => openDeliveryModal(null)} {...revampModalPrimaryButtonStyles}>
                  New schedule
                </Button>
              )}
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>

      {canExport && (
        <DeliveryDialog
          key={editingDeliveryId ?? 'new-delivery'}
          isOpen={deliveryModal.isOpen}
          onClose={deliveryModal.onClose}
          modelId={modelId}
          dashboardId={dashboardId}
          dashboardName={dashboardName}
          parameterSet={renderContext}
          existing={editingDelivery}
          schedules={packDeliveries}
          onSwitchSchedule={(id) => setEditingDeliveryId(id)}
          onSaved={(saved) => {
            setEditingDeliveryId(saved.id);
            setDeliveries((current) => {
              const rest = current.filter((row) => row.id !== saved.id);
              return [saved, ...rest];
            });
            setDeliveriesLoaded(true);
          }}
        />
      )}
    </>
  );
};

export default DashboardExportPanel;
